import { zodResolver } from "@hookform/resolvers/zod";
import { Entry } from "contentful";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { DateTime } from "ts-luxon";

import Container from "~/components/common/container";
import Grid from "~/components/common/grid";
import HighlightedText from "~/components/common/highlighted-text";
import Errors from "~/components/form/errors";
import { useConfiguration } from "~/contexts/configuration";
import useGiftcard from "~/hooks/use-giftcard";
import Logger from "~/services/logger/logger";
import {
  CheckBalanceFormFields,
  checkBalanceFormFieldsSchema,
  CheckBalanceResponse,
} from "~/shared/giftcard/check-balance.model";
import { ZodSchemas } from "~/shared/giftcard/zod-schemas.class";
import Editorials from "~/types/editorials";
import contentfulUtils from "~/utils/contentful-utils";
import { assertEditorialType } from "~/utils/editorial-utils";
import intlUtils from "~/utils/intl-utils";
import IntlUtils from "~/utils/intl-utils";

import InputField from "../form/input-field";
import Button from "./button";
import styles from "./check-your-balance.module.scss";
import EscapeNewLine from "./escape-new-line";
import MediaAsset from "./media-asset";

type Props = {
  entry: Entry<unknown>;
};

export default function CheckYourBalance({ entry }: Props) {
  assertEditorialType<Editorials.CheckYourBalance>(entry, "checkYourBalance");
  const { title, pretitle, description, image, buttonLabel, codeInputTooltip, pinInputTooltip } = entry.fields;
  const t = useTranslations();
  const priceFormatter = intlUtils.usePriceFormatter();
  const { configuration, organizationUnitId } = useConfiguration();
  const [cardData, setCardData] = useState<CheckBalanceResponse | undefined>();
  const { doCheckGiftcardBalance, giftcardServiceError } = useGiftcard();
  const giftcardConfigId = configuration?.AvailableGiftCardConfigurations.find(
    (config: { Name: string }) => config.Name === process.env.NEXT_PUBLIC_GIFTCARD_CONFIGURATION_NAME
  )?.ID;

  const inspectorMode = contentfulUtils.useInspectorMode(entry);

  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CheckBalanceFormFields>({
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldFocusError: false,
    resolver: zodResolver(checkBalanceFormFieldsSchema),
  });

  const onSubmit: SubmitHandler<CheckBalanceFormFields> = async (fields: CheckBalanceFormFields) => {
    Logger.instance.debug("check-your-balance fields", fields);
    const response = await doCheckGiftcardBalance({
      ...fields,
      ConfigurationID: giftcardConfigId!,
      OrganizationUnitID: organizationUnitId,
    });
    if (response) {
      reset();
      setCardData(response);
    }
  };

  const formSection = (
    <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
      <div className={styles.formInputs}>
        <InputField
          {...register("CardNumber")}
          type="text"
          label={t("generic.checkbalance.form_code")}
          error={errors.CardNumber?.message ? t(errors.CardNumber.message) : undefined}
          onClearErrors={() => clearErrors("CardNumber")}
          className={styles.inputFieldContainer}
          tooltip={codeInputTooltip}
        />
        <InputField
          {...register("CardPin")}
          type={"text"}
          label={t("generic.checkbalance.form_pin")}
          error={errors.CardPin?.message ? t(errors.CardPin.message) : undefined}
          maxLength={ZodSchemas.pinMaxlength}
          onClearErrors={() => clearErrors("CardPin")}
          className={styles.inputFieldContainer}
          tooltip={pinInputTooltip}
        />
      </div>
      <Errors error={giftcardServiceError}></Errors>
      <Button
        variant="secondary"
        type="submit"
        disabled={isSubmitting}
        loading={isSubmitting}
        className={styles.loginButton}
        {...inspectorMode?.getProps("buttonLabel")}
      >
        <EscapeNewLine text={buttonLabel} />
      </Button>
    </form>
  );

  return (
    <Container className={styles.wrapper}>
      <Grid className={styles.grid}>
        <div className={styles.col1}>
          {cardData ? (
            <>
              <h2 className={styles.titleHighlighted}>
                <HighlightedText text={t("generic.checkbalance.your_balance_title")} />
              </h2>
              <div className={styles.cardBalanceBox}>
                <p className={styles.cardBalanceTitle}>{t("generic.checkbalance.your_card_number")}</p>
                <p className={styles.cardBalanceValue}>{cardData.CardID}</p>
                <p className={styles.cardBalanceTitle}>{t("generic.checkbalance.your_balance")}</p>
                <p className={styles.cardBalanceValue}>
                  {priceFormatter.currency(cardData.RemainingBalance || cardData.Balance, {
                    currency: cardData.CurrencyID,
                  })}
                </p>
                {cardData.Activation && (
                  <>
                    <p className={styles.cardBalanceTitle}>{t("generic.checkbalance.activation")}</p>
                    <p className={styles.cardBalanceValue}>
                      {DateTime.fromMillis(cardData.Activation).toFormat(IntlUtils.defaultDateFormat)}
                    </p>
                  </>
                )}
                {cardData.Expiry && (
                  <>
                    <p className={styles.cardBalanceTitle}>{t("generic.checkbalance.expiry")}</p>
                    <p className={styles.cardBalanceValue}>
                      {DateTime.fromMillis(cardData.Expiry).toFormat(IntlUtils.defaultDateFormat)}
                    </p>
                  </>
                )}
              </div>
              <p className={styles.pretitleDesktop}>{t("generic.checkbalance.check_another_card")}</p>
              {formSection}
            </>
          ) : (
            <>
              <div className={styles.checkBalanceText}>
                {pretitle && (
                  <p {...inspectorMode?.getProps("pretitle")} className={styles.pretitleDesktop}>
                    <EscapeNewLine text={pretitle} />
                  </p>
                )}
                {title &&
                  (contentfulUtils.isHighlightText(title) ? (
                    <h2 {...inspectorMode?.getProps("title")} className={styles.titleHighlighted}>
                      <HighlightedText text={title} />
                    </h2>
                  ) : (
                    <h2 {...inspectorMode?.getProps("title")} className={styles.title}>
                      <EscapeNewLine text={title} />
                    </h2>
                  ))}
                <p {...inspectorMode?.getProps("description")} className={styles.description}>
                  <EscapeNewLine text={description} />
                </p>
              </div>
              {formSection}
            </>
          )}
        </div>
        <div className={styles.col2}>
          <div className={styles.imageWrapper}>
            <MediaAsset entry={image} />
          </div>
        </div>
      </Grid>
    </Container>
  );
}
